"use client";

import { IconCheckOutline18, IconPlugOutline18 } from "nucleo-ui-outline-18";
import { usePipedreamClient } from "@/app/(v2)/connectors/_components/usePipedreamClient";
import ConnectorFormModal from "@/app/(v2)/connectors/_components/ConnectorFormModal";
import { useConnectorsStore } from "@/stores/connectorsStore";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

interface ConnectorAccountSelectorProps {
  label: string;
  description?: string;
  value: string;
  onChange: (value: string) => void;
  /** The connector type to filter/create, e.g. "pipedream:gmail", "pipedream:twilio" */
  connectorType?: string;
}

export default function ConnectorAccountSelector({
  label,
  description,
  value,
  onChange,
  connectorType,
}: ConnectorAccountSelectorProps) {
  const connectors = useConnectorsStore(s => s.connectors);
  const connectorTypes = useConnectorsStore(s => s.connectorTypes);
  const loadConnectors = useConnectorsStore(s => s.loadConnectors);
  const loadConnectorTypes = useConnectorsStore(s => s.loadConnectorTypes);
  const createConnector = useConnectorsStore(s => s.createConnector);

  const { isConnecting, error, clearError, connectAccount } = usePipedreamClient();
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [loaded, setLoaded] = useState(false);

  // Load connectors and types on mount
  useEffect(() => {
    Promise.all([loadConnectors(), loadConnectorTypes()]).finally(() => setLoaded(true));
  }, [loadConnectors, loadConnectorTypes]);

  // Find matching connectors and type config
  const matchingConnectors = useMemo(
    () => (connectorType ? connectors.filter(c => c.type === connectorType) : []),
    [connectors, connectorType],
  );

  const connectorTypeConfig = useMemo(
    () => (connectorType ? connectorTypes.find(ct => ct.type === connectorType) : undefined),
    [connectorTypes, connectorType],
  );

  const isPipedream = connectorTypeConfig?.provider === "pipedream";
  const displayName = connectorTypeConfig?.name || connectorType?.replace(/^pipedream:/, "") || "Account";

  // Auto-select if only one connector and value is either empty or stale
  // (pointing at a connector that no longer exists in the store).
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    const valueIsValid = value && matchingConnectors.some(c => c.id === value);
    if (!valueIsValid && matchingConnectors.length === 1) {
      onChangeRef.current(matchingConnectors[0].id);
    }
  }, [matchingConnectors, value]);

  const selectedConnector = useMemo(() => matchingConnectors.find(c => c.id === value), [matchingConnectors, value]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleConnect = useCallback(async () => {
    if (!connectorTypeConfig) return;

    if (isPipedream) {
      // Direct OAuth flow for Pipedream connectors
      const appSlug = connectorTypeConfig.providerConfig?.appSlug;
      if (!appSlug) return;

      clearError();
      setCreateError(null);
      const result = await connectAccount({ appSlug });
      if (!result) return;

      setIsCreating(true);
      try {
        const connector = await createConnector({
          type: connectorType!,
          name: displayName,
          description: "",
          data: { accountId: result.accountId },
        });
        await loadConnectors();
        onChange(connector.id);
        setIsOpen(false);
      } catch (err) {
        const message = err instanceof Error ? err.message : `Failed to create ${displayName} connector.`;
        setCreateError(message);
        console.error(`Failed to create ${displayName} connector:`, err);
      } finally {
        setIsCreating(false);
      }
    } else {
      // Open the connector form modal for non-Pipedream connectors
      setShowModal(true);
    }
  }, [
    connectorTypeConfig,
    isPipedream,
    clearError,
    connectAccount,
    createConnector,
    connectorType,
    displayName,
    loadConnectors,
    onChange,
  ]);

  // Called after modal creates a connector
  const handleModalClose = useCallback(async () => {
    setShowModal(false);
    await loadConnectors();
  }, [loadConnectors]);

  // After connectors reload, auto-select the newly added connector
  const prevIdsRef = useRef(new Set(matchingConnectors.map(c => c.id)));
  useEffect(() => {
    if (!loaded) {
      prevIdsRef.current = new Set(matchingConnectors.map(c => c.id));
      return;
    }
    const currentIds = new Set(matchingConnectors.map(c => c.id));
    const newConnector = matchingConnectors.find(c => !prevIdsRef.current.has(c.id));
    if (newConnector && !showModal) {
      onChangeRef.current(newConnector.id);
    }
    prevIdsRef.current = currentIds;
  }, [matchingConnectors, showModal, loaded]);

  const isBusy = isConnecting || isCreating;

  if (!loaded) {
    return (
      <div className="space-y-2">
        {label && <label className="field-label block">{label}</label>}
        <div className="border-ds-border-default text-ds-text-disabled dark:bg-ds-neutral-800 flex items-center gap-2 rounded-lg border bg-zinc-50 px-3 py-2 text-sm">
          <PixelLoader size="sm" />
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2" ref={containerRef}>
      {label && <label className="field-label block">{label}</label>}
      {description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>}
      <div className="relative">
        {matchingConnectors.length === 0 ? (
          /* No connectors — show connect button */
          <button
            type="button"
            onClick={handleConnect}
            disabled={isBusy || !connectorTypeConfig}
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-blue-300 bg-blue-50 px-3 py-3 text-sm font-medium text-blue-700 transition-colors hover:border-blue-400 hover:bg-blue-100 disabled:opacity-60 dark:border-blue-600 dark:bg-blue-900/20 dark:text-blue-300 dark:hover:border-blue-500 dark:hover:bg-blue-900/30"
          >
            {isBusy ? (
              <>
                <PixelLoader size="sm" />
                {isCreating ? "Setting up..." : "Connecting..."}
              </>
            ) : (
              <>
                <IconPlugOutline18 className="h-4 w-4" />
                Connect {displayName}
              </>
            )}
          </button>
        ) : matchingConnectors.length === 1 ? (
          /* Single connector — show as selected */
          <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2 dark:border-green-800 dark:bg-green-900/20">
            <IconCheckOutline18 className="h-4 w-4 flex-shrink-0 text-green-600 dark:text-green-400" />
            <IconPlugOutline18 className="h-4 w-4 flex-shrink-0 text-green-600 dark:text-green-400" />
            <span className="min-w-0 flex-1 truncate text-sm text-green-800 dark:text-green-200">
              {selectedConnector?.name || matchingConnectors[0].name}
            </span>
            <button
              type="button"
              onClick={handleConnect}
              disabled={isBusy}
              className="flex-shrink-0 text-xs text-green-700 hover:text-green-900 dark:text-green-300 dark:hover:text-green-100"
            >
              {isBusy ? "Connecting..." : "Add another"}
            </button>
          </div>
        ) : (
          /* Multiple connectors — show dropdown */
          <>
            <button
              type="button"
              onClick={() => setIsOpen(!isOpen)}
              onKeyDown={e => {
                if (e.key === "Escape") setIsOpen(false);
              }}
              aria-haspopup="listbox"
              aria-expanded={isOpen}
              className="border-ds-neutral-300 text-ds-text-primary dark:border-ds-neutral-700 bg-ds-surface-card flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm hover:border-zinc-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:hover:border-zinc-600"
            >
              {selectedConnector ? (
                <span className="flex items-center gap-2">
                  <IconPlugOutline18 className="h-4 w-4 text-blue-500" />
                  <span>{selectedConnector.name}</span>
                </span>
              ) : (
                <span className="text-ds-text-disabled dark:text-ds-text-muted">Select a {displayName} account...</span>
              )}
              <svg className="text-ds-text-disabled h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {isOpen && (
              <div
                role="listbox"
                className="border-ds-border-default bg-ds-surface-card shadow-ds-elev-2 absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg border"
              >
                {matchingConnectors.map(connector => (
                  <button
                    key={connector.id}
                    type="button"
                    role="option"
                    aria-selected={connector.id === value}
                    onClick={() => {
                      onChange(connector.id);
                      setIsOpen(false);
                    }}
                    className={`hover:bg-ds-surface-hover flex w-full items-center gap-2 px-3 py-2 text-left text-sm ${
                      connector.id === value
                        ? "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                        : "text-ds-text-primary"
                    }`}
                  >
                    <IconPlugOutline18 className="h-4 w-4 flex-shrink-0 opacity-60" />
                    <span className="min-w-0 flex-1 truncate">{connector.name}</span>
                    {connector.id === value && <IconCheckOutline18 className="h-4 w-4 flex-shrink-0 text-blue-500" />}
                  </button>
                ))}

                <button
                  type="button"
                  onClick={handleConnect}
                  disabled={isBusy}
                  className="border-ds-border-default flex w-full items-center gap-2 border-t px-3 py-2 text-left text-sm text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
                >
                  <span className="text-base leading-none">+</span>
                  <span>{isBusy ? "Connecting..." : `Connect another ${displayName}`}</span>
                </button>
              </div>
            )}
          </>
        )}
      </div>
      {(error || createError) && <p className="micro-label text-red-600 dark:text-red-400">{error || createError}</p>}
      {/* Connector form modal for non-Pipedream connectors */}
      {showModal && connectorTypeConfig && (
        <ConnectorFormModal
          isOpen={showModal}
          onClose={handleModalClose}
          connectorTypes={[connectorTypeConfig]}
          preselectedType={connectorType}
        />
      )}
    </div>
  );
}
